import { Component, Inject, LOCALE_ID, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { ChildOrderRuleDto } from 'src/app/api/models/child-order-rule';
import {
  startOfMonth, addMonths, getYear, getMonth, isSameMonth, isBefore, format, max,
} from 'date-fns';
import { pt } from 'date-fns/locale';

registerLocaleData(localePt);

interface MonthEntry {
  month: string;
  year: number;
  hasDelivery: boolean;
  deliveryNumber?: number;
  date?: Date;
  isSelected?: boolean;
  disabled?: boolean;
  isRuleDefined?: boolean;
}

export interface SplitOrderResult {
  entregas: number;
  deliveryDates: Date[];
}

@Component({
    selector: 'app-split-order-dialog',
    templateUrl: './split-order-dialog.component.html',
    styleUrls: ['./split-order-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SplitOrderDialogComponent {
  public entregas: number;
  public months: MonthEntry[] = [];
  public selectedDates: Date[] = [];
  public isRuleDefined: boolean[] = [];

  get isEntregasDefinedByRules(): boolean {
    return !!(this.data.childOrderRules && this.data.childOrderRules.length > 0);
  }

  constructor(
    public dialogRef: MatDialogRef<SplitOrderDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      entregas: number;
      startDate: Date;
      limiteEntrega: Date;
      childOrderRules?: ChildOrderRuleDto[] | null;
    },
    @Inject(LOCALE_ID) private locale: string
  ) {
    this.entregas = this.data.entregas;
    this.updateCalendar();
  }

  updateCalendar() {
    const today = startOfMonth(new Date());
    const start = new Date(this.data.startDate);
    const effectiveStart = max([today, start]);
    const end = new Date(this.data.limiteEntrega);
    this.months = [];

    // Calculate total months between start and end date
    const startMonth = getMonth(effectiveStart);
    const startYear = getYear(effectiveStart);
    const endMonth = getMonth(end);
    const endYear = getYear(end);

    let totalMonths: number;
    if (endYear > startYear) {
      totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth + 1);
    } else {
      totalMonths = endMonth - startMonth + 1;
    }

    // Ensure we don't try to create more deliveries than months
    if (totalMonths < this.entregas) {
      this.entregas = totalMonths;
    }

    // Generate all months between start and end date
    for (let m = 0; m < totalMonths; m++) {
      const currentMonth = addMonths(effectiveStart, m);
      const utcDate = new Date(Date.UTC(getYear(currentMonth), getMonth(currentMonth), 1));

      // Check if the month is August (month index 7)
      const isAugust = getMonth(currentMonth) === 7;

      this.months.push({
        month: format(currentMonth, 'MMM', { locale: pt }),
        year: getYear(currentMonth),
        hasDelivery: false,
        deliveryNumber: undefined,
        date: utcDate,
        isSelected: false,
        disabled: isAugust
      });
    }

    // Clear any previous selections
    this.selectedDates = new Array(this.entregas);
    this.isRuleDefined = new Array(this.entregas).fill(false);
    this.months.forEach(m => {
      m.hasDelivery = false;
      m.deliveryNumber = undefined;
      m.isSelected = false;
      m.isRuleDefined = false;
    });

    // Initialize from childOrderRules if available
    if (this.data.childOrderRules && this.data.childOrderRules.length > 0) {
      for (let i = 0; i < this.entregas; i++) {
        const entrega = i + 1;
        const rule = this.data.childOrderRules.find(r => r.entrega === entrega);

        if (rule && rule.dataEntrega) {
          // Convert to Date if it's a string (JSON sends dates as strings)
          const ruleDate = rule.dataEntrega instanceof Date
            ? rule.dataEntrega
            : new Date(rule.dataEntrega as any);
          const defaultDate = new Date('1900-01-01');

          // Check if rule date is valid (not default)
          if (ruleDate > defaultDate && !isNaN(ruleDate.getTime())) {
            // Find matching month entry
            const ruleMonth = startOfMonth(ruleDate);
            const matchingMonth = this.months.find(m => {
              return isSameMonth(m.date!, ruleMonth);
            });

            if (matchingMonth && !matchingMonth.disabled) {
              matchingMonth.hasDelivery = true;
              matchingMonth.deliveryNumber = entrega;
              matchingMonth.isSelected = true;
              matchingMonth.isRuleDefined = true;
              this.selectedDates[i] = matchingMonth.date!;
              this.isRuleDefined[i] = true;
            } else if (isBefore(ruleMonth, effectiveStart)) {
              // Rule date is in the past — push to first available month
              const fallbackMonth = this.months.find(m => !m.disabled && !m.isSelected);
              if (fallbackMonth) {
                fallbackMonth.hasDelivery = true;
                fallbackMonth.deliveryNumber = entrega;
                fallbackMonth.isSelected = true;
                fallbackMonth.isRuleDefined = true;
                this.selectedDates[i] = fallbackMonth.date!;
                this.isRuleDefined[i] = true;
              }
            }
          }
        }
      }
    }

    // Fill remaining dates with regular interval logic if not all are rule-defined
    const filledCount = this.selectedDates.filter(d => d !== undefined).length;
    if (filledCount < this.entregas) {
      // Calculate the interval between deliveries based on total months
      const interval = Math.floor(totalMonths / this.entregas);

      // Set deliveries at regular intervals for non-rule-defined dates
      for (let i = 0; i < this.entregas; i++) {
        // Skip if already filled by rule
        if (this.selectedDates[i] !== undefined) {
          continue;
        }

        // Calculate the target index for this delivery
        const targetIndex = i * interval;

        // If targetIndex is beyond our months array, use the last month
        if (targetIndex >= this.months.length) {
          continue;
        }

        // Get the month at the target index
        let monthEntry = this.months[targetIndex];

        // Skip if already selected by a rule
        if (monthEntry.isSelected) {
          continue;
        }

        // If this falls on August (disabled), move to July
        if (monthEntry.disabled) {
          // Try to use July (previous month)
          if (targetIndex > 0) {
            monthEntry = this.months[targetIndex - 1];
          } else if (this.months.length > 1) {
            // If we can't go back, try going forward to September
            monthEntry = this.months[targetIndex + 1];
          }
        }

        // Skip if already selected
        if (monthEntry.isSelected) {
          continue;
        }

        // Set this month as having a delivery
        monthEntry.hasDelivery = true;
        monthEntry.deliveryNumber = i + 1;
        monthEntry.isSelected = true;
        this.selectedDates[i] = monthEntry.date!;
      }

      // Ensure first delivery is at the start if not rule-defined
      if (this.entregas > 0 && !this.isRuleDefined[0]) {
        const firstAvailableIndex = this.months.findIndex(m => !m.disabled && !m.isSelected);
        if (firstAvailableIndex !== -1) {
          // Clear any previously set first delivery (if not rule-defined)
          this.months.forEach(m => {
            if (m.deliveryNumber === 1 && !m.isRuleDefined) {
              m.hasDelivery = false;
              m.deliveryNumber = undefined;
              m.isSelected = false;
            }
          });

          // Set the first available month to have the first delivery
          const firstMonth = this.months[firstAvailableIndex];
          firstMonth.hasDelivery = true;
          firstMonth.deliveryNumber = 1;
          firstMonth.isSelected = true;
          this.selectedDates[0] = firstMonth.date!;
        }
      }
    }
  }

  onEntregasChange() {
    this.updateCalendar();
  }

  isFormValid(): boolean {
    const selectedMonthsCount = this.months.filter((m) => m.isSelected).length;
    return (
      selectedMonthsCount === this.entregas &&
      this.entregas > 0 &&
      this.entregas <= this.data.entregas
    );
  }

  onMonthClick(month: MonthEntry) {
    // Rule dates are now SUGGESTIONS, not locks. The user can click any month
    // (rule-defined or not) to toggle it. The only hard block is the August disabled
    // flag and the "max selections reached" guard.
    // Spec: § Rule dates are suggestions, not locks
    if (month.disabled) return;

    if (month.isSelected) {
      month.isSelected = false;
      month.hasDelivery = false;
      month.deliveryNumber = undefined;
    } else {
      const selectedCount = this.months.filter((m) => m.isSelected).length;
      if (selectedCount >= this.entregas) {
        return;
      }
      month.isSelected = true;
      month.hasDelivery = true;
    }

    // Sort by date and assign delivery numbers strictly by chronological order.
    // No rule-defined preservation — the user is free to override.
    const selectedMonths = this.months
      .filter((m) => m.isSelected)
      .sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0));

    selectedMonths.forEach((m, idx) => {
      m.deliveryNumber = idx + 1;
    });

    this.months
      .filter((m) => !m.isSelected)
      .forEach((m) => {
        m.deliveryNumber = undefined;
      });

    // Rebuild selectedDates strictly from the user's current chronological selection.
    const newSelectedDates = new Array(this.entregas);
    selectedMonths.forEach((m) => {
      if (m.deliveryNumber && m.date) {
        const dateIndex = m.deliveryNumber - 1;
        newSelectedDates[dateIndex] = new Date(Date.UTC(m.year, m.date.getUTCMonth(), 1));
      }
    });

    this.selectedDates = newSelectedDates;
  }

  getRemainingSelectionsText(): string {
    const selectedCount = this.months.filter((m) => m.isSelected).length;
    const remaining = this.entregas - selectedCount;

    if (remaining === 0) {
      return 'Todas as entregas selecionadas';
    } else if (remaining === this.entregas) {
      return `Selecione ${this.entregas} meses para as entregas`;
    } else {
      return `Selecione mais ${remaining} ${
        remaining === 1 ? 'mês' : 'meses'
      } para as entregas`;
    }
  }

  confirm(): void {
    const result: SplitOrderResult = {
      entregas: this.entregas,
      deliveryDates: this.selectedDates,
    };
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
